/**
 * TemplateTableColumns — the columns of the templates list. Demo page: /tables/data-tables (name cell with a subtitle,
 * badges, a count column, a row ⋯ menu). Built on `DataTable`; the page owns the actions.
 */
import { Link } from 'react-router-dom';
import { ExternalLink, Pencil, Power, PowerOff, Trash2, Upload } from 'lucide-react';
import { ActionMenu, Badge, type BadgeVariant, type DataTableColumn } from '../../components/ui/index.js';
import { Can } from '../../components/auth/Can.js';
import type { AgentTemplateSummary } from '../../generated/api/types.gen.js';
import { sentenceCase } from '../../components/ui/FilterSelect.js';

export type TemplateRow = AgentTemplateSummary;

const DEPARTMENT_VARIANT: Record<string, BadgeVariant> = {
  research: 'info',
  risk: 'danger',
  execution: 'success',
  compliance: 'warning',
  orchestration: 'purple',
};

const SOURCE_VARIANT: Record<string, BadgeVariant> = {
  system: 'info',
  org: 'neutral',
  community: 'purple',
  marketplace: 'success',
};

/** The one status a row shows: a disabled template reads "Disabled" whatever its stored status. */
export function templateStatus(row: TemplateRow): string {
  return row.disabled ? 'disabled' : (row.status ?? 'active');
}

export function templateDepartment(row: TemplateRow): string {
  return row.departmentId ?? 'unassigned';
}

interface ColumnActions {
  /** Publishing to the marketplace is gated by a different policy than editing. */
  canPublish: boolean;
  onOpen: (row: TemplateRow) => void;
  onToggleStatus: (row: TemplateRow) => void;
  onDelete: (row: TemplateRow) => void;
  onPublish: (row: TemplateRow) => void;
}

export function buildTemplateColumns({
  canPublish,
  onOpen,
  onToggleStatus,
  onDelete,
  onPublish,
}: ColumnActions): DataTableColumn<TemplateRow>[] {
  return [
    {
      key: 'title',
      header: 'Template',
      width: '28%',
      sortable: true,
      sortValue: (row) => row.title.toLowerCase(),
      render: (row) => (
        <div className="min-w-0">
          <Link
            to={`/agents/templates/${row.slug}`}
            className={`text-[13px] font-semibold transition-colors hover:text-accent-text ${row.disabled ? 'text-muted line-through' : 'text-heading'}`}
          >
            {row.title}
          </Link>
          {row.goal && <p className="mt-0.5 line-clamp-1 max-w-[360px] text-[11px] text-muted">{row.goal}</p>}
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
      sortValue: templateDepartment,
      render: (row) => (
        <Badge variant={DEPARTMENT_VARIANT[templateDepartment(row)] ?? 'neutral'}>
          {sentenceCase(templateDepartment(row))}
        </Badge>
      ),
    },
    {
      key: 'platform',
      header: 'Platform',
      sortable: true,
      sortValue: (row) => row.platform ?? '',
      render: (row) =>
        row.platform ? (
          <Badge variant="neutral">{sentenceCase(row.platform)}</Badge>
        ) : (
          <span className="text-faint">Any</span>
        ),
    },
    {
      key: 'source',
      header: 'Source',
      sortable: true,
      sortValue: (row) => row.source ?? '',
      render: (row) =>
        row.source ? (
          <Badge variant={SOURCE_VARIANT[row.source] ?? 'neutral'}>{sentenceCase(row.source)}</Badge>
        ) : (
          <span className="text-faint">None</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortValue: templateStatus,
      render: (row) => {
        const status = templateStatus(row);
        return (
          <Badge variant={status === 'active' ? 'success' : 'danger'} dot>
            {sentenceCase(status)}
          </Badge>
        );
      },
    },
    {
      key: 'tools',
      header: 'Tools',
      align: 'right',
      sortable: true,
      sortValue: (row) => row.tools?.length ?? 0,
      render: (row) => <span className="text-body tabular-nums">{row.tools?.length ?? 0}</span>,
    },
    {
      key: 'usedBy',
      header: 'Used by',
      align: 'right',
      sortable: true,
      sortValue: (row) => row.workflowCount ?? 0,
      render: (row) => {
        const count = row.workflowCount ?? 0;
        return count === 0 ? (
          <span className="text-faint">None</span>
        ) : (
          <span
            className="text-body tabular-nums"
            title={`${count} workflow${count === 1 ? '' : 's'} use this template`}
          >
            {count} {count === 1 ? 'workflow' : 'workflows'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '56px',
      render: (row) => (
        <div className="flex justify-end">
          <Can
            policy="CanManageAgentTemplates"
            fallback={
              <Link
                to={`/agents/templates/${row.slug}`}
                aria-label={`Open ${row.title}`}
                className="text-icon-secondary hover:text-heading"
              >
                <ExternalLink size={14} aria-hidden="true" />
              </Link>
            }
          >
            <ActionMenu
              aria-label={`Actions for ${row.title}`}
              items={[
                {
                  id: 'edit',
                  label: 'Open and edit',
                  icon: <Pencil size={14} />,
                  onAction: () => onOpen(row),
                },
                ...(canPublish
                  ? [
                      {
                        id: 'publish',
                        label: 'Publish to marketplace',
                        icon: <Upload size={14} />,
                        onAction: () => onPublish(row),
                      },
                    ]
                  : []),
                {
                  id: 'status',
                  label: row.disabled ? 'Enable' : 'Disable',
                  icon: row.disabled ? <Power size={14} /> : <PowerOff size={14} />,
                  onAction: () => onToggleStatus(row),
                },
                {
                  id: 'delete',
                  label: 'Delete',
                  icon: <Trash2 size={14} />,
                  tone: 'danger',
                  onAction: () => onDelete(row),
                },
              ]}
            />
          </Can>
        </div>
      ),
    },
  ];
}
